import { GameState } from '../game/GameState';
import { DataRegistry } from './DataRegistry';
import { GoodsManager } from './GoodsManager';
import { ModifierManager } from './ModifierManager';

export interface TradeRouteSummary {
  provinceId: string;
  owner: string;
  controller: string;
  value: number;
  goods: string[];
  blocked: boolean;
  blockadedBy?: string;
  imports: number;
  exports: number;
  transportCost: number;
  pricePressure: number;
}

export class TradeManager {
  static readonly instance = new TradeManager();

  routeSummaries(): TradeRouteSummary[] {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    return Object.values(campaign.provinces)
      .filter((province) => registry.provinces[province.id]?.port)
      .map((province) => {
        const goodsValue = GoodsManager.instance.valueOfStockpile(province.storage);
        const modifiers = ModifierManager.instance.forFaction(province.owner);
        const ledger = province.lastEconomy;
        const imports = this.flowValue(ledger?.imports ?? {}, province.marketPrices);
        const exports = this.flowValue(ledger?.exports ?? {}, province.marketPrices);
        const prices = registry.goodList().map((good) => (province.marketPrices[good.id] ?? good.basePrice) / Math.max(0.01, good.basePrice));
        const pricePressure = prices.length ? prices.reduce((sum, value) => sum + value, 0) / prices.length : 1;
        const marketLevels = province.buildings
          .filter((id) => ['market', 'warehouse', 'customs_house', 'harbor'].includes(id))
          .reduce((sum, id) => sum + (province.buildingLevels[id] ?? 1), 0);
        const ownerState = campaign.factions[province.owner];
        const diplomaticTradeMultiplier = 1 + (ownerState?.treaties.trade.length ?? 0) * 0.025 + (ownerState?.treaties.tradeConcessions.length ?? 0) * 0.06;
        const routeValue = Math.max(4, Math.round((goodsValue / 35 + exports * 0.35 + (province.portLevel ?? 1) * 3 + marketLevels * 2 + Math.floor(province.prosperity / 20) + modifiers.portTradeBonus) * modifiers.tradeMultiplier * diplomaticTradeMultiplier));
        const embargoed = ownerState?.atWarWith.some((enemyId) => campaign.factions[enemyId]?.treaties.embargoes?.includes(province.owner)) ?? false;
        const blocked = embargoed || Boolean(province.blockadedBy);
        const occupationMultiplier = province.controller === province.owner ? 1 : 0.55;
        const blockadeMultiplier = province.blockadedBy ? 0.2 : embargoed ? 0.5 : 1;
        const exportGoods = Object.entries(ledger?.exports ?? {}).sort((a, b) => b[1] - a[1]).map(([id]) => id);
        return {
          provinceId: province.id,
          owner: province.owner,
          controller: province.controller,
          value: Math.max(0, Math.floor(routeValue * occupationMultiplier * blockadeMultiplier)),
          goods: exportGoods.length ? exportGoods.slice(0, 4) : registry.provinces[province.id]?.deposits ?? [],
          blocked,
          blockadedBy: province.blockadedBy,
          imports: Math.round(imports),
          exports: Math.round(exports),
          transportCost: ledger?.transportCost ?? 0,
          pricePressure: Math.round(pricePressure * 100) / 100,
        };
      });
  }

  processMonth() {
    const campaign = GameState.instance.requireCampaign();
    for (const route of this.routeSummaries()) {
      const owner = campaign.factions[route.owner];
      const controller = campaign.factions[route.controller];
      if (!owner) continue;
      if (route.controller === route.owner) owner.treasury += route.value;
      else {
        owner.treasury += Math.floor(route.value * 0.25);
        if (controller) controller.treasury += Math.floor(route.value * 0.45);
      }
    }
  }

  signTradeAgreement(sourceFactionId: string, targetFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    if (!source || !target) return false;
    if (!source.treaties.trade.includes(targetFactionId)) source.treaties.trade.push(targetFactionId);
    if (!target.treaties.trade.includes(sourceFactionId)) target.treaties.trade.push(sourceFactionId);
    GameState.instance.log(`Trade agreement signed with ${DataRegistry.instance.factions[targetFactionId]?.name}.`, 'success');
    return true;
  }

  private flowValue(goods: Record<string, number>, prices: Record<string, number>) {
    return Object.entries(goods).reduce((sum, [goodId, amount]) => sum + amount * (prices[goodId] ?? DataRegistry.instance.goods[goodId]?.basePrice ?? 1), 0);
  }
}
